import { ModalProvider } from "@/components/modal";
import { getShopFromLocalStorage, shopQuery, useShop } from "@/hooks/use-shop";
import { useUser } from "@/hooks/use-user";
import { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useNavigate,
  useLocation,
} from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { i18n } from "@/lib/i18n";
import { z } from "zod";
import { LoadingSpinner } from "@/components/spinner.tsx";
import posthog from "posthog-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { Footer, Page, PageContent } from "@/components/page";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useRecyclerFillEvents,
  type RecyclerFillEvent,
} from "@/hooks/use-recycler-fill-events";
import { useLanguage } from "@/hooks/use-language";
import { getApiClient } from "@/lib/api-client";
import { CountryCodeContext } from "@frontend-common/country-provider";
import { LocaleContext } from "@frontend-common/locale-provider";
import { ApiClientContext } from "@frontend-common/api-client-provider";
import { FeatureFlagsContext } from "@frontend-common/feature-flags-provider";
import { featureFlags } from "@common/feature-flags";
import type { Country } from "@common/logic/services/country-service";
import type { Language2 } from "@common/logic/services/language-service";
import { getServerUrl, useIsCashAgentAvailable } from "@/lib/server";
import { getCashAgentUrl, getConfiguredKioskKey } from "@/lib/runtime-config";
import { useToken } from "@/hooks/use-token";
import * as Sentry from "@sentry/react";
import {
  getTrackedSessionId,
  recordTrackedEvent,
  updateTrackedSession,
} from "@/lib/kiosk-session-tracking";
import { CashPaymentDebugPanel } from "@/features/payment-cash/cash-payment-debug-panel";
import { KioskAudio } from "@/features/audio/kiosk-audio";
import { KioskTalkBanner } from "@/features/kiosk-talk/kiosk-talk";
import { NthPurchaseCampaignBanner } from "@/features/nth-purchase-campaign/nth-purchase-campaign-banner";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    validateSearch: z
      .object({
        client_id: z
          .string()
          .nonempty()
          .catch(() => getShopFromLocalStorage()?.kioskKey ?? ""),
        kioskKey: z
          .string()
          .nonempty()
          .catch(() => getShopFromLocalStorage()?.kioskKey ?? ""),
      })
      .transform((elem) => {
        const kioskKey =
          getConfiguredKioskKey() ||
          elem.kioskKey ||
          elem.client_id ||
          localStorage.getItem("kioskKeyBackup") ||
          "";
        if (kioskKey) {
          localStorage.setItem("kioskKeyBackup", kioskKey);
        }
        return { kioskKey };
      }),
    beforeLoad: async ({ context, search }) => {
      try {
        const shop = await context.queryClient.ensureQueryData(
          shopQuery(search.kioskKey),
        );
        posthog.identify(shop.shopId.toString());
        Sentry.setTag("kiosk_id", shop.shopId.toString());
      } catch (err) {
        console.error(err);
        // throw notFound();
      }
    },
    pendingComponent: () => <LoadingSpinner />,
    component: RootComponent,
    errorComponent: ErrorPage,
    notFoundComponent: ErrorPage,
  },
);

function RootComponent() {
  useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useLanguage();
  const shop = useShop();
  const user = useUser();
  const token = useToken();
  const previousPathRef = useRef<string | null>(null);
  const previousLanguageRef = useRef(lang);
  const previousUserIdRef = useRef<string | undefined>(undefined);
  const countryCode = (
    shop.data.currencyCode === "EUR" ? "sk" : "hu"
  ) as Country;
  const isCashAgentAvailable = useIsCashAgentAvailable();
  const client = getApiClient({
    locale: lang,
    countryCode,
    kioskKey: shop.data.kioskKey,
    token,
    serverUrl: isCashAgentAvailable.data
      ? getCashAgentUrl()
      : getServerUrl(),
  });

  // Only listen for navigation events when not already on recycler-refill page
  const isOnRecyclerRefill = location.pathname === "/recycler-refill";

  // Global listener for recycler fill navigation events
  useRecyclerFillEvents({
    filter: (event) => event.type === "navigate",
    onEvent: useCallback(
      (event: RecyclerFillEvent) => {
        if (event.type === "navigate" && !isOnRecyclerRefill) {
          console.log("Navigating to:", event.path);
          navigate({ to: event.path });
        }
      },
      [navigate, isOnRecyclerRefill],
    ),
  });

  useEffect(() => {
    if (location.pathname === previousPathRef.current) {
      return;
    }

    previousPathRef.current = location.pathname;

    if (location.pathname === "/" || location.pathname === "/start-screen") {
      return;
    }

    void recordTrackedEvent({
      eventType: "screen_view",
      routePath: location.pathname,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!getTrackedSessionId()) {
      previousLanguageRef.current = lang;
      return;
    }

    if (previousLanguageRef.current === lang) {
      return;
    }

    previousLanguageRef.current = lang;
    void updateTrackedSession({
      current_language: lang,
      last_route_path: location.pathname,
    });
  }, [lang, location.pathname]);

  useEffect(() => {
    const userId = user.data?.id;

    if (!getTrackedSessionId()) {
      previousUserIdRef.current = userId;
      return;
    }

    if (!userId || previousUserIdRef.current === userId) {
      return;
    }

    previousUserIdRef.current = userId;
    void updateTrackedSession({
      user_id: Number(userId),
      last_route_path: location.pathname,
    });
  }, [location.pathname, user.data?.id]);

  return (
    <CountryCodeContext.Provider value={countryCode}>
      <LocaleContext.Provider value={lang as Language2}>
        <ApiClientContext.Provider value={client}>
          <FeatureFlagsContext.Provider value={featureFlags}>
            <ModalProvider>
              <Outlet />
              <KioskAudio />
              <KioskTalkBanner />
              <NthPurchaseCampaignBanner shopId={shop.data.shopId} />
              <CashPaymentDebugPanel />
            </ModalProvider>
          </FeatureFlagsContext.Provider>
        </ApiClientContext.Provider>
      </LocaleContext.Provider>
    </CountryCodeContext.Provider>
  );
}

function ErrorPage() {
  const [secs, setSecs] = useState(10);
  useTranslation();
  const navigate = useNavigate();

  // Set language to the shop's default locale
  useEffect(() => {
    const shop = getShopFromLocalStorage();
    if (shop?.locale) {
      i18n.changeLanguage(shop.locale);
    }
  }, []);

  posthog.capture("404", {
    client_id: localStorage.getItem("client_id"),
    kioskKey: localStorage.getItem("kioskKey"),
  });
  useEffect(() => {
    const interval = setInterval(() => {
      setSecs((secs) => secs - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  if (secs <= 0) {
    navigate({
      to: "/start-screen",
      search: (prev) => ({
        ...prev,
        kioskKey: localStorage.getItem("kioskKeyBackup"),
      }),
      reloadDocument: true,
    });
  }

  return (
    <Page skipInactvityModal>
      <PageContent>
        <div className="flex flex-col items-center justify-center h-screen gap-8 px-12">
          <div className="rounded-full p-8 bg-red-100">
            <AlertTriangle
              className="h-20 w-20 text-red-600"
              strokeWidth={1.5}
            />
          </div>
          <h2 className="font-helvetica font-bold text-5xl text-center text-gray-800">
            <Trans>Hiba történt, kérjük várjon</Trans>
          </h2>
          <div className="flex items-center gap-3 text-3xl text-gray-500">
            <RefreshCw className="h-8 w-8 animate-spin" />
            <span>
              <Trans count={secs}>
                Új próbálkozás {{ count: secs }} másodperc múlva…
              </Trans>
            </span>
          </div>
          <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mt-4">
            <div
              className={cn(
                "h-full rounded-full transition-all duration-1000 ease-linear",
                "bg-red-500",
              )}
              style={{ width: `${((10 - secs) / 10) * 100}%` }}
            />
          </div>
        </div>
      </PageContent>
      <Footer />
    </Page>
  );
}
